import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type INormalizedApiError } from 'shared/api'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type ISurveySettings } from 'entities/Surveys/api/types'
import {
  EnumVariantMessageField,
  MessageFieldStore,
  makeSavedReplyInputAction,
} from 'widgets/MessageField'
import { SavedRepliesStore } from 'widgets/SavedReplies'
import { SurveyMessageActionsStore } from 'pages/surveys/ui/SurveyView/store/surveyMessageActionsStore'
import { CategoryMessageStore } from 'pages/surveys/ui/SurveyView/ui/CategoryMessage/store/categoryMessageStore'
import { resolveSurveyBodyAttachments } from 'pages/surveys/lib/reconstructSurveyAttachments'
import { NPS_PREVIEW_REPLY_SCORE, getNpsCategoryKeyByScore } from 'pages/surveys/lib/npsCategories'

export const SCORE_ELABORATION_DEFAULT_MESSAGE =
  'Thanks for your response. Can you tell us a bit more why you chose this score?'

type IScoreElaborationMessageValue = ISurveySettings['score_elaboration_message']['value']

export class ScoreElaborationStore {
  enabled = false
  perGroupEnabled = false
  error = ''
  readonly messageFieldStore: MessageFieldStore
  readonly promoters: CategoryMessageStore
  readonly passives: CategoryMessageStore
  readonly detractors: CategoryMessageStore
  private readonly _savedRepliesStore: SavedRepliesStore
  private readonly _messageActions: SurveyMessageActionsStore
  private _disposeErrorReset: IReactionDisposer | null = null
  private _disposed = false

  constructor(private _entities: IEntitiesStore) {
    this._savedRepliesStore = new SavedRepliesStore(this._entities)
    this.promoters = new CategoryMessageStore('score_elaboration_promoters', _entities)
    this.passives = new CategoryMessageStore('score_elaboration_passives', _entities)
    this.detractors = new CategoryMessageStore('score_elaboration_detractors', _entities)

    this.messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Write your message...',
        styles: { minHeight: 62 },
        variant: EnumVariantMessageField.Default,
        showActionsItems: 4,
        resetOnUnmount: false,
        textLimit: {
          maxLength: 1600,
          message: 'Message should not be longer than 1.600 characters',
        },
        makeInputActions: (store) => [makeSavedReplyInputAction(store, this._savedRepliesStore)],
      },
      this._entities
    )

    this._messageActions = new SurveyMessageActionsStore(
      this.messageFieldStore,
      this._savedRepliesStore,
      this._entities
    )

    this.messageFieldStore.setActions(this._messageActions.actions)

    makeAutoObservable(this)

    this._writeToField(SCORE_ELABORATION_DEFAULT_MESSAGE, [])
    this._reactionErrorReset()
  }

  get message() {
    return this.messageFieldStore.messageInnerText
  }

  get isModified() {
    return (
      this.message.trim() !== SCORE_ELABORATION_DEFAULT_MESSAGE.trim() ||
      this.messageFieldStore.attachments.length > 0
    )
  }

  get paramsMessage(): IScoreElaborationMessageValue {
    return {
      message: this.messageFieldStore.text,
      media_url: this.messageFieldStore.media_url,
    }
  }

  get categories() {
    return [this.promoters, this.passives, this.detractors]
  }

  get sectionError() {
    if (this.enabled && this.perGroupEnabled) {
      return this.error || this.categories.find((category) => category.error)?.error || ''
    }
    return this.error
  }

  get params() {
    if (this.enabled && this.perGroupEnabled) {
      return {
        score_elaboration: { value: true },
        score_elaboration_per_group: { value: true },
        score_elaboration_message: { value: this.paramsMessage },
        score_elaboration_promoters: { value: this.promoters.paramsValue },
        score_elaboration_passives: { value: this.passives.paramsValue },
        score_elaboration_detractors: { value: this.detractors.paramsValue },
      }
    }

    return {
      score_elaboration: { value: this.enabled },
      score_elaboration_per_group: { value: false },
      score_elaboration_message: { value: this.paramsMessage },
      score_elaboration_promoters: { value: null },
      score_elaboration_passives: { value: null },
      score_elaboration_detractors: { value: null },
    }
  }

  private get _previewCategory() {
    return this[getNpsCategoryKeyByScore(NPS_PREVIEW_REPLY_SCORE)]
  }

  get previewHtml() {
    if (this.perGroupEnabled) return this._previewCategory.previewHtml
    return this.message ? this.messageFieldStore.messageHtml : ''
  }

  get previewAttachments() {
    if (this.perGroupEnabled) return this._previewCategory.previewAttachments
    return this.messageFieldStore.attachments
  }

  get hasPreviewContent() {
    if (this.perGroupEnabled) return this._previewCategory.hasMessage
    return Boolean(this.message) || this.messageFieldStore.attachments.length > 0
  }

  init = (settings: ISurveySettings | null | undefined) => {
    this.enabled = Boolean(settings?.score_elaboration?.value)
    this.perGroupEnabled = Boolean(settings?.score_elaboration_per_group?.value)

    const value = settings?.score_elaboration_message?.value
    const message = value?.message || SCORE_ELABORATION_DEFAULT_MESSAGE
    const attachments = (value?.media_url ?? []).map(
      (requestAttachment) => new Attachment({ requestAttachment, fromMediaUrl: true })
    )
    this._writeToField(message, attachments)
    void this._restoreBodyAttachments(message, value?.media_url ?? [])

    this.promoters.init(settings?.score_elaboration_promoters?.value)
    this.passives.init(settings?.score_elaboration_passives?.value)
    this.detractors.init(settings?.score_elaboration_detractors?.value)
    this.setError('')
  }

  private _restoreBodyAttachments = async (
    message: string,
    mediaUrl: IScoreElaborationMessageValue['media_url']
  ) => {
    const attachments = await resolveSurveyBodyAttachments(this._entities, message, mediaUrl)
    if (this._disposed || !attachments.length) return
    this.messageFieldStore.addAttachments(attachments)
  }

  setEnabled = (value: boolean) => {
    this.enabled = value
  }

  setPerGroupEnabled = (value: boolean) => {
    this.perGroupEnabled = value
    this.setError('')
  }

  setError = (value: string) => {
    this.error = value
    if (this.perGroupEnabled) {
      this.messageFieldStore.clearHighlight()
      return
    }
    if (value) this.messageFieldStore.setHighlight('error')
    else this.messageFieldStore.clearHighlight()
  }

  validate = () => {
    if (!this.enabled) {
      this.setError('')
      return true
    }

    if (this.perGroupEnabled) {
      if (!this.categories.some((category) => category.hasMessage)) {
        this.setError('Please enter at least one message')
        return false
      }
      const invalidCategories = this.categories.filter(
        (category) => !category.validate('Please enter score elaboration message')
      )
      if (invalidCategories.length) {
        this.setError('')
        return false
      }
      this.setError('')
      return true
    }

    if (!this.message.trim()) {
      this.setError('Please enter score elaboration message')
      return false
    }
    this.setError('')
    return true
  }

  syncApiErrors = (apiErrors: INormalizedApiError | null | undefined) => {
    const match = apiErrors?.entries().find(([key]) => key.includes('score_elaboration_message'))
    this.setError(match?.[1]?.value ?? '')
    this.promoters.syncApiErrors(apiErrors)
    this.passives.syncApiErrors(apiErrors)
    this.detractors.syncApiErrors(apiErrors)
  }

  revertToDefault = () => {
    this._writeToField(SCORE_ELABORATION_DEFAULT_MESSAGE, [])
  }

  dispose = () => {
    this._disposed = true
    this._messageActions.dispose()
    this._disposeErrorReset?.()
    this.messageFieldStore.reset()
    this.promoters.dispose()
    this.passives.dispose()
    this.detractors.dispose()
  }

  private _writeToField = (value: string, attachments: Attachment[]) => {
    this.messageFieldStore.setMessageData({
      message: value,
      attachments,
      isReplace: true,
      isFocus: false,
      replaceNewRow: false,
    })
  }

  private _reactionErrorReset = () => {
    this._disposeErrorReset = reaction(
      () => ({
        enabled: this.enabled,
        perGroup: this.perGroupEnabled,
        hasInput: this.perGroupEnabled
          ? this.categories.some((category) => category.hasMessage)
          : Boolean(this.messageFieldStore.messageInnerText.trim()),
      }),
      ({ enabled, hasInput }) => {
        if (!this.error) return
        if (!enabled || hasInput) this.setError('')
      },
      {
        equals: (a, b) =>
          a.enabled === b.enabled && a.perGroup === b.perGroup && a.hasInput === b.hasInput,
      }
    )
  }
}
